import type { AutocompleteListItem } from '../../types';

/**
 * Creates a freeform autocomplete item from the user's query.
 * Used when allowFreeformHandlebars is enabled and the provider doesn't recognize the syntax.
 *
 * Handles three patterns:
 * - Block start: #blockType [args...] -> {{#blockType args}}
 * - Block end: /blockType -> {{/blockType}}
 * - Inline: expression -> {{expression}}
 */
export function createFreeformItem(query: string): AutocompleteListItem & { isComplete: boolean } {
  const trimmedQuery = query.trim();

  // Detect block start pattern: #blockType [args...]
  if (trimmedQuery.startsWith('#')) {
    const parts = trimmedQuery.slice(1).split(/\s+/);
    const blockType = parts[0];
    const variable = parts.slice(1).join(' ');
    return {
      id: '__freeform_block__',
      label: `{{#${blockType}${variable ? ' ' + variable : ''}}}`,
      value: variable,
      description: 'Insert as block helper',
      isBlockType: true,
      blockType: blockType,
      isComplete: true,
    };
  }

  // Detect block end pattern: /blockType
  if (trimmedQuery.startsWith('/')) {
    const blockType = trimmedQuery.slice(1).trim();
    return {
      id: '__freeform_block_end__',
      label: `{{/${blockType}}}`,
      value: blockType,
      description: 'Insert closing tag',
      isComplete: true,
      metadata: { isBlockEnd: true, blockType },
    };
  }

  // Regular inline tag (could include @root, helpers with args, etc.)
  return {
    id: '__freeform__',
    label: `{{${trimmedQuery}}}`,
    value: trimmedQuery,
    description: 'Insert as template expression',
    isComplete: true,
  };
}

/**
 * Create an error item for display in the autocomplete list.
 */
export function createErrorItem(message: string): AutocompleteListItem & { isError: boolean } {
  return {
    id: '__error__',
    label: 'Error',
    description: message,
    value: '',
    isError: true,
  };
}

/**
 * Create a warning item for display alongside freeform items.
 */
export function createWarningItem(message: string): AutocompleteListItem & { isError: boolean } {
  return {
    id: '__warning__',
    label: 'Warning',
    description: message,
    value: '',
    isError: true,
  };
}
